import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Clipboard, Filter, Plus, Terminal } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { env } from "@/app/env";
import { useAppsQuery, useEnrollmentTokensQuery, useIssueEnrollmentTokenMutation, usePublicKeyQuery } from "@/api/query-hooks";
import type { EnrollmentTokenRecord, EnrollmentTokenStatus, IssuedEnrollmentTokenRecord } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { ClientScopePicker, useTenantClientScope } from "@/features/licensing/client-scope";
import { ClientIdentity } from "@/features/licensing/client-identity";
import { formatDateTime } from "@/lib/date";
import { positiveIntegerSearch } from "@/lib/search";

type EnrollmentSearch = {
  appId?: string;
  clientId?: number;
  status?: string;
};

const ttlOptions = ["30m", "1h", "4h", "24h", "168h"] as const;
const releaseChannels = ["STABLE", "BETA", "ALPHA"] as const;

const issueSchema = z.object({
  appId: z.string().trim().min(1),
  clientId: z.coerce.number().int().min(1),
  ttl: z.enum(ttlOptions),
  platformUrl: z.string().trim().url(),
  baseDir: z.string().trim().min(1).max(240),
  releaseChannel: z.enum(releaseChannels)
});

type IssueFormValues = z.infer<typeof issueSchema>;

type IssuedCommand = {
  token: IssuedEnrollmentTokenRecord;
  command: string;
};

export function EnrollmentsPage() {
  const { t, i18n } = useTranslation(["enrollments", "common"]);
  const navigate = useNavigate({ from: "/enrollments" });
  const search = useSearch({ from: "/enrollments" });
  const params = useMemo(() => enrollmentParams(search), [search]);
  const scope = useTenantClientScope(search.clientId);
  const appsQuery = useAppsQuery();
  const publicKeyQuery = usePublicKeyQuery();
  const tokensQuery = useEnrollmentTokensQuery(params, scope.ready);
  const mutation = useIssueEnrollmentTokenMutation();
  const [issued, setIssued] = useState<IssuedCommand | null>(null);

  const form = useForm<IssueFormValues>({
    resolver: zodResolver(issueSchema),
    defaultValues: {
      appId: "mailarchiver",
      clientId: scope.clientId ?? 0,
      ttl: "1h",
      platformUrl: env.apiBaseUrl || "http://127.0.0.1:3010",
      baseDir: "/var/lib/mailarchiver",
      releaseChannel: "STABLE"
    }
  });

  useEffect(() => {
    if (scope.clientId !== undefined) {
      form.setValue("clientId", scope.clientId, { shouldValidate: true });
    }
  }, [form, scope.clientId]);

  const submit = form.handleSubmit((values) => {
    const publicKey = publicKeyQuery.data?.publicKey;
    if (!publicKey) {
      toast.error(t("publicKeyUnavailable"));
      return;
    }
    mutation.mutate(
      {
        appId: values.appId,
        clientId: values.clientId,
        ttl: values.ttl
      },
      {
        onSuccess: (token) => {
          setIssued({
            token,
            command: buildMailarchiverCommand({
              token: token.token,
              platformUrl: values.platformUrl,
              platformPublicKey: publicKey,
              baseDir: values.baseDir,
              releaseChannel: values.releaseChannel
            })
          });
          toast.success(t("issued"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  });

  if (scope.isLoading || (scope.ready && tokensQuery.isLoading) || appsQuery.isLoading || publicKeyQuery.isLoading) {
    return <LoadingState />;
  }
  if (scope.isError) {
    return <ErrorState title={t("common:states.error")} detail={scope.error instanceof Error ? scope.error.message : undefined} onRetry={() => void scope.refetch()} />;
  }
  if (scope.ready && tokensQuery.isError) {
    return <ErrorState title={t("common:states.error")} detail={tokensQuery.error.message} onRetry={() => void tokensQuery.refetch()} />;
  }
  if (appsQuery.isError) {
    return <ErrorState title={t("common:states.error")} detail={appsQuery.error.message} onRetry={() => void appsQuery.refetch()} />;
  }
  if (publicKeyQuery.isError) {
    return <ErrorState title={t("common:states.error")} detail={publicKeyQuery.error.message} onRetry={() => void publicKeyQuery.refetch()} />;
  }

  const apps = appsQuery.data?.items ?? [];
  const tokens = tokensQuery.data?.items ?? [];

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t("title")}</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
        </div>
      </div>

      {scope.tenant ? (
        <section className="rounded-md border bg-panel p-3">
          <ClientScopePicker value={search.clientId} required={scope.required} onChange={(clientId) => void navigate({ search: { ...search, clientId } })} />
        </section>
      ) : null}

      <section className="rounded-md border bg-panel p-4">
        {scope.tenant && scope.clientId === undefined ? <p className="mb-3 text-sm text-muted-foreground">{t("common:clientScope.issueHint")}</p> : null}
        <form className="grid gap-3" onSubmit={submit}>
          <div className="grid gap-3 lg:grid-cols-[1fr_140px_1fr]">
            <Field label={t("app")}>
              <Select value={form.watch("appId")} onValueChange={(value) => form.setValue("appId", value, { shouldDirty: true, shouldValidate: true })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {apps.map((app) => (
                    <SelectItem key={app.id} value={app.id}>
                      {app.id}
                    </SelectItem>
                  ))}
                  {apps.length === 0 ? <SelectItem value="mailarchiver">mailarchiver</SelectItem> : null}
                </SelectContent>
              </Select>
            </Field>
            {!scope.tenant ? <Field label={t("client")}><Input type="number" inputMode="numeric" {...form.register("clientId")} /></Field> : null}
            <Field label={t("ttl")}>
              <Select value={form.watch("ttl")} onValueChange={(value) => form.setValue("ttl", value as IssueFormValues["ttl"], { shouldDirty: true, shouldValidate: true })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ttlOptions.map((ttl) => (
                    <SelectItem key={ttl} value={ttl}>
                      {ttl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("platformUrl")}>
              <Input {...form.register("platformUrl")} />
            </Field>
          </div>
          <div className="grid gap-3 lg:grid-cols-[1fr_180px_auto]">
            <Field label={t("baseDir")}>
              <Input {...form.register("baseDir")} />
            </Field>
            <Field label={t("releaseChannel")}>
              <Select
                value={form.watch("releaseChannel")}
                onValueChange={(value) => form.setValue("releaseChannel", value as IssueFormValues["releaseChannel"], { shouldDirty: true, shouldValidate: true })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {releaseChannels.map((channel) => (
                    <SelectItem key={channel} value={channel}>
                      {channel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={mutation.isPending || (scope.tenant && scope.clientId === undefined)}>
                <Plus className="h-4 w-4" />
                {t("issue")}
              </Button>
            </div>
          </div>
        </form>
      </section>

      {issued ? <IssuedTokenPanel issued={issued} language={i18n.language} /> : null}

      <section className="rounded-md border bg-panel p-3">
        <div className="grid gap-3 lg:grid-cols-[1fr_160px_auto]">
          <FilterField label={t("app")}>
            <Input
              value={search.appId ?? ""}
              placeholder="mailarchiver"
              onChange={(event) => void navigate({ search: { ...search, appId: event.target.value || undefined } })}
            />
          </FilterField>
          {!scope.tenant ? <FilterField label={t("client")}><Input value={search.clientId ?? ""} inputMode="numeric" onChange={(event) => void navigate({ search: { ...search, clientId: positiveIntegerSearch(event.target.value) } })} /></FilterField> : null}
          <FilterField label={t("status")}>
            <Select
              value={search.status ?? "ALL"}
              onValueChange={(value) => void navigate({ search: { ...search, status: value === "ALL" ? undefined : (value as EnrollmentTokenStatus) } })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("allStatuses")}</SelectItem>
                <SelectItem value="UNUSED">{t("statuses.UNUSED")}</SelectItem>
                <SelectItem value="USED">{t("statuses.USED")}</SelectItem>
                <SelectItem value="EXPIRED">{t("statuses.EXPIRED")}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
          <div className="flex items-end">
            <Button variant="outline" disabled={!scope.ready} onClick={() => void tokensQuery.refetch()}>
              {t("common:actions.refresh")}
            </Button>
          </div>
        </div>
      </section>

      {tokens.length === 0 ? (
        <EmptyState title={t("common:states.empty")} />
      ) : (
        <section className="overflow-hidden rounded-md border bg-panel">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("status")}</th>
                  <th className="px-3 py-2">{t("app")}</th>
                  <th className="px-3 py-2">{t("client")}</th>
                  <th className="px-3 py-2">{t("issuedBy")}</th>
                  <th className="px-3 py-2">{t("issuedAt")}</th>
                  <th className="px-3 py-2">{t("expiresAt")}</th>
                  <th className="px-3 py-2">{t("usedAt")}</th>
                  <th className="px-3 py-2">{t("station")}</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <EnrollmentTokenRow key={token.id} token={token} language={i18n.language} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function IssuedTokenPanel({ issued, language }: { issued: IssuedCommand; language: string }) {
  const { t } = useTranslation(["enrollments", "common"]);
  return (
    <section className="grid gap-3 rounded-md border bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{t("issuedTitle")}</h3>
          <div className="mt-1 text-sm text-muted-foreground">
            {issued.token.appId} / <ClientIdentity clientId={issued.token.clientId} compact /> / {formatDateTime(issued.token.expiresAt, language)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void copyText(issued.token.token, t("copied"))}>
            <Clipboard className="h-4 w-4" />
            {t("copyToken")}
          </Button>
          <Button onClick={() => void copyText(issued.command, t("copied"))}>
            <Terminal className="h-4 w-4" />
            {t("copyCommand")}
          </Button>
        </div>
      </div>
      <Field label={t("token")}>
        <Input readOnly value={issued.token.token} className="font-mono text-xs" />
      </Field>
      <Field label={t("command")}>
        <Textarea readOnly value={issued.command} className="min-h-40 font-mono text-xs" />
      </Field>
    </section>
  );
}

function EnrollmentTokenRow({ token, language }: { token: EnrollmentTokenRecord; language: string }) {
  const { t } = useTranslation("enrollments");
  return (
    <tr className="border-t">
      <td className="px-3 py-2">
        <Badge variant={statusVariant(token.status)}>{t(`statuses.${token.status}`)}</Badge>
      </td>
      <td className="px-3 py-2 font-mono text-xs">{token.appId}</td>
      <td className="px-3 py-2"><ClientIdentity clientId={token.clientId} /></td>
      <td className="max-w-60 truncate px-3 py-2">{token.issuedByEmployeeId}</td>
      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(token.issuedAt, language)}</td>
      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(token.expiresAt, language)}</td>
      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(token.usedAt, language)}</td>
      <td className="max-w-60 truncate px-3 py-2 font-mono text-xs">{token.usedByStationId || "—"}</td>
    </tr>
  );
}

function enrollmentParams(search: EnrollmentSearch) {
  const params = new URLSearchParams({ limit: "100" });
  if (search.appId) {
    params.set("appId", search.appId);
  }
  if (search.clientId) {
    params.set("clientId", String(search.clientId));
  }
  if (isEnrollmentTokenStatus(search.status)) {
    params.set("status", search.status);
  }
  return params;
}

function isEnrollmentTokenStatus(value: string | undefined): value is EnrollmentTokenStatus {
  return value === "UNUSED" || value === "USED" || value === "EXPIRED";
}

function buildMailarchiverCommand(input: {
  token: string;
  platformUrl: string;
  platformPublicKey: string;
  baseDir: string;
  releaseChannel: string;
}) {
  return [
    `MAILARCHIVER_LICENSE_PLATFORM_URL=${shellQuote(input.platformUrl)} \\`,
    `MAILARCHIVER_LICENSE_PLATFORM_PUBLIC_KEY=${shellQuote(input.platformPublicKey)} \\`,
    `MAILARCHIVER_LICENSE_STATE_KEY="$(openssl rand -base64 32 | tr '+/' '-_' | tr -d '=')" \\`,
    `MAILARCHIVER_LICENSE_TOKEN=${shellQuote(input.token)} \\`,
    `mailarchiver --base-dir ${shellQuote(input.baseDir)} --json license enroll --release-channel ${shellQuote(input.releaseChannel)}`
  ].join("\n");
}

function shellQuote(value: string) {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

async function copyText(value: string, message: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Clipboard is unavailable");
  }
}

function statusVariant(status: EnrollmentTokenStatus): "success" | "destructive" | "warning" {
  if (status === "USED") {
    return "success";
  }
  if (status === "EXPIRED") {
    return "destructive";
  }
  return "warning";
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        <Filter className="h-3 w-3" />
        {label}
      </Label>
      {children}
    </div>
  );
}
